import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Bookmark, CalendarDays, GanttChart, Globe, Kanban, List, Lock, Search, Users } from 'lucide-react'
import { ViewRowActions } from './ViewRowActions'
import type { SavedFilterDocument } from '@/lib/db/rxdb/hooks'
import type {SavedFilterScope} from '@/lib/types/savedFilter';
import type { ViewType } from '@/store/ProjectViewPreferencesStore'
import { Input } from '@/components/ui/input'
import {
  useProjectSavedFilters,
  useSpaceSavedFilters,
} from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import {
  
  parseSavedLayoutConfig
} from '@/lib/types/savedFilter'

const VIEW_TYPE_ICONS: Record<ViewType, typeof List> = {
  list: List,
  board: Kanban,
  calendar: CalendarDays,
  gantt: GanttChart,
}

const PERMISSION_LABEL = {
  private: { icon: Lock, label: 'Private' },
  workspace: { icon: Users, label: 'Workspace' },
  project: { icon: Globe, label: 'Shared' },
} as const

interface ViewsListTableProps {
  scope: SavedFilterScope
  /** The space or project id — used for scoped queries. */
  scopeEntityId: string
  /** URL slug used to build links to /views/$viewId within the current scope. */
  scopeKey: string
  workspaceSlug: string
  /**
   * When rendering project views inside a space URL, this is the enclosing
   * space's projectKey — views then link to the space-scoped URL so the user
   * doesn't lose context.
   */
  spaceKey?: string
}

export function ViewsListTable({
  scope,
  scopeEntityId,
  scopeKey,
  workspaceSlug,
  spaceKey,
}: ViewsListTableProps) {
  const navigate = useNavigate()
  const userId = useLoginStore((s) => s.userId)

  const projectQuery = useProjectSavedFilters(
    scope === 'project' ? scopeEntityId : undefined,
    scope === 'project' ? userId : undefined,
  )
  const spaceQuery = useSpaceSavedFilters(
    scope === 'space' ? scopeEntityId : undefined,
    scope === 'space' ? userId : undefined,
  )
  const views =
    scope === 'space' ? spaceQuery.savedFilters : projectQuery.savedFilters

  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!views) return []
    const term = search.trim().toLowerCase()
    if (!term) return views
    return views.filter((v) => {
      const haystack = `${v.name} ${v.description ?? ''}`.toLowerCase()
      return haystack.includes(term)
    })
  }, [views, search])

  const handleOpen = (view: SavedFilterDocument) => {
    if (scope === 'space') {
      navigate({
        to: '/$workspaceSlug/spaces/$spaceKey/views/$viewId',
        params: { workspaceSlug, spaceKey: scopeKey, viewId: view.id },
      })
    } else if (spaceKey) {
      navigate({
        to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/views/$viewId',
        params: { workspaceSlug, spaceKey, projectKey: scopeKey, viewId: view.id },
      })
    } else {
      navigate({
        to: '/$workspaceSlug/projects/$projectKey/views/$viewId',
        params: { workspaceSlug, projectKey: scopeKey, viewId: view.id },
      })
    }
  }

  const hasViews = (views?.length ?? 0) > 0

  return (
    <div className="flex h-full flex-col">
      {hasViews ? (
        <div className="flex items-center gap-2 px-4 md:px-8 py-3 border-b border-border">
          <div className="relative flex-1 max-w-md">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search views..."
              className="h-8 pl-7 text-sm"
            />
          </div>
        </div>
      ) : null}

      <div className="flex-1 min-h-0 overflow-auto">
        {filtered.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-muted-foreground">
            <Bookmark className="size-8 opacity-40" />
            <p className="text-sm">
              {search
                ? 'No views match your search.'
                : 'No views yet — save the current filters and layout as a view.'}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-background border-b border-border text-xs text-muted-foreground">
              <tr>
                <th className="px-4 md:px-8 py-2 text-left font-medium">Name</th>
                <th className="px-4 py-2 text-left font-medium w-24">
                  <div className="flex items-center gap-1.5">
                    <span aria-hidden className="size-3.5 shrink-0" />
                    <span>Layout</span>
                  </div>
                </th>
                <th className="px-4 py-2 text-left font-medium w-32">
                  <div className="flex items-center gap-1.5">
                    <span aria-hidden className="size-3.5 shrink-0" />
                    <span>Permission</span>
                  </div>
                </th>
                <th className="px-4 py-2 text-right font-medium w-28">Last Updated</th>
                <th className="px-2 py-2 w-10">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((view) => {
                const layout = parseSavedLayoutConfig(view.layoutConfig)
                const viewType: ViewType = layout?.viewType ?? 'list'
                const LayoutIcon = VIEW_TYPE_ICONS[viewType]
                const permissionMeta = PERMISSION_LABEL[view.permission]
                const PermissionIcon = permissionMeta.icon
                return (
                  <tr
                    key={view.id}
                    className="border-b border-border/60 hover:bg-accent/40 cursor-pointer"
                    onClick={() => handleOpen(view)}
                  >
                    <td className="px-4 md:px-8 py-2">
                      <div className="flex flex-col">
                        <span className="font-medium text-foreground truncate">
                          {view.name}
                        </span>
                        {view.description ? (
                          <span className="text-xs text-muted-foreground truncate">
                            {view.description}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <LayoutIcon className="size-3.5" />
                        <span className="capitalize text-xs">{viewType}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <PermissionIcon className="size-3.5" />
                        <span className="text-xs">{permissionMeta.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right text-xs text-muted-foreground">
                      {new Date(view.updatedOn).toLocaleDateString()}
                    </td>
                    <td
                      className="px-2 py-2 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ViewRowActions view={view} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

    </div>
  )
}
